/**
 * Загрузка кредитов и лизинга из Excel: чистые функции диалога
 * `DebtsImportDialog` — сводка графика договора, подписи, договор к записи.
 */
import type {
  CeoDebtImportContract,
  CeoDebtImportItem,
  CeoDebtImportSheet,
  CeoDebtInstrumentType,
} from '@shared/api';
import { formatDateRu, formatMoney } from '../ceoFormat';

/** Файлы, которые принимает разбор. */
export const ACCEPT_DEBTS_FILE = '.xlsx,.xlsm,.csv';

/** Типы договоров — в порядке списка «Тип» (подписи — `INSTRUMENT_TYPE_LABELS`). */
export const DEBT_TYPES: readonly CeoDebtInstrumentType[] = [
  'credit_line_vkl',
  'credit_line_nkl',
  'term_loan',
  'overdraft',
  'project_finance',
  'leasing',
  'bank_guarantee',
];

/** Что взято с листа файла. */
export const SHEET_LAYOUT_LABELS: Record<CeoDebtImportSheet['layout'], string> = {
  register: 'список договоров',
  schedule: 'график платежей',
  blocks: 'графики по договорам',
  skipped: 'пропущен',
};

/** Сводка графика платежей договора: сами строки графика не выводим (их тысячи). */
export interface ScheduleSummary {
  count: number;
  /** Дата последнего платежа, YYYY-MM-DD. */
  lastDue: string | null;
  /** Платежей с датой раньше сегодняшней без отметки об оплате. */
  pastUnmarked: number;
  /** Остаток основного долга по графику, если такие платежи считать неоплаченными. */
  unpaid: number;
  /** Остаток основного долга по графику, если такие платежи считать оплаченными. */
  unpaidPastPaid: number;
}

/**
 * Неоплаченный основной долг платежа — как его посчитает ОСНОВА при записи:
 * сумма без разбивки на долг и проценты целиком идёт в долг (у лизинга
 * остаток — сумма будущих платежей), частичная оплата гасит долг пропорционально.
 */
function unpaidPrincipal(s: CeoDebtImportContract['schedule'][number], paid: number): number {
  const total = Math.max(0, s.total_payment);
  const principal =
    s.principal_amount ?? Math.max(0, total - (s.interest_amount ?? 0) - (s.fee_amount ?? 0));
  if (total <= 0) return principal;
  if (paid + 0.01 >= total) return 0;
  return principal * (1 - Math.min(1, Math.max(0, paid) / total));
}

export function summarizeSchedule(c: CeoDebtImportContract, today: string): ScheduleSummary {
  let lastDue: string | null = null;
  let pastUnmarked = 0;
  let unpaid = 0;
  let unpaidPastPaid = 0;
  for (const s of c.schedule) {
    if (lastDue === null || s.due_date > lastDue) lastDue = s.due_date;
    // Даты — YYYY-MM-DD: строки сравниваются как даты.
    const unmarkedPast = s.paid_amount == null && s.due_date < today;
    if (unmarkedPast) pastUnmarked += 1;
    const paid = s.paid_amount ?? 0;
    unpaid += unpaidPrincipal(s, paid);
    unpaidPastPaid += unpaidPrincipal(s, unmarkedPast ? s.total_payment : paid);
  }
  return { count: c.schedule.length, lastDue, pastUnmarked, unpaid, unpaidPastPaid };
}

/** Договор к записи: как пришёл из разбора, с выбранным типом. */
export function toImportItem(
  c: CeoDebtImportContract,
  type: CeoDebtInstrumentType,
  typeChosen: boolean,
  buildSchedule: boolean,
): CeoDebtImportItem {
  return {
    ...c,
    instrument_type: type,
    // Тип выбран в просмотре (даже тот же, что предложила программа) — он
    // уже не догадка.
    derived: typeChosen ? c.derived.filter((f) => f !== 'instrument_type') : c.derived,
    action: c.existing_id ? 'update' : 'create',
    build_schedule: buildSchedule && c.schedule.length === 0,
  };
}

/** 1 платёж, 2 платежа, 5 платежей. */
export function plural(n: number, forms: readonly [string, string, string]): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return forms[0];
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return forms[1];
  return forms[2];
}

const RUBLE_CODES = new Set(['RUB', 'RUR', '₽', 'РУБ', 'РУБ.', 'РУБЛЬ', 'РУБЛИ', 'РУБЛЕЙ']);

/** Сумма в валюте договора: рубли — «1 250 000 ₽», иначе — с кодом валюты. */
export function moneyText(value: number, currency: string | null): string {
  const code = (currency ?? '').trim();
  const text = formatMoney(value);
  if (!code || RUBLE_CODES.has(code.toUpperCase())) return text;
  return text.replace(/\s*₽$/, ` ${code}`);
}

export function pctText(value: number): string {
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 3 })}%`;
}

/** «19,5%», для плавающей — «КС + 2,5%». */
export function rateText(c: CeoDebtImportContract): string {
  if (c.rate_type === 'floating_cbr') {
    if (c.margin_pct == null || c.margin_pct === 0) return 'КС';
    return c.margin_pct > 0 ? `КС + ${pctText(c.margin_pct)}` : `КС − ${pctText(-c.margin_pct)}`;
  }
  return c.base_rate_pct == null ? '—' : pctText(c.base_rate_pct);
}

/** «15.01.2026 – 15.01.2027». */
export function termText(start: string | null, end: string | null): string {
  if (start && end) return `${formatDateRu(start)} – ${formatDateRu(end)}`;
  if (start) return `с ${formatDateRu(start)}`;
  if (end) return `до ${formatDateRu(end)}`;
  return '—';
}

/** «12 платежей, до 15.01.2027» или «нет графика». */
export function scheduleText(s: ScheduleSummary): string {
  if (s.count === 0) return 'нет графика';
  const count = `${s.count} ${plural(s.count, ['платёж', 'платежа', 'платежей'])}`;
  return s.lastDue ? `${count}, до ${formatDateRu(s.lastDue)}` : count;
}
